import clsx from 'clsx';
import React from 'react';

/**
 * Bars: a tokens-only bar chart for one series over time — daily minutes in
 * a billing period, minutes per period. Plain elements, no chart library,
 * no tooltips carrying information that is not also on the page; the
 * totals live in the figures beside it, this shows the *shape*.
 *
 * Bars are `accent`. An optional `reference` (the included minutes) draws a
 * dashed hairline with its label; the part of a bar above it is a warning
 * tint. When one bar is `selected` the others step back.
 */
export interface Bar {
  key: string;
  /** Axis label ("Sep 12", "Aug"). */
  label: string;
  value: number;
  selected?: boolean;
}

export const Bars: React.FC<{
  bars: Bar[];
  reference?: { value: number; label: string };
  format: (n: number) => string;
  /** Plot height in px (labels add ~20px). Default 160. */
  height?: number;
  /** Max axis labels drawn; the rest are thinned evenly. Default 8. */
  maxLabels?: number;
  className?: string;
  'aria-label'?: string;
}> = ({
  bars,
  reference,
  format,
  height = 160,
  maxLabels = 8,
  className,
  ...rest
}) => {
  const maxVal = bars.reduce((m, b) => Math.max(m, b.value), 0);
  const ref = reference?.value ?? 0;
  const top = Math.max(maxVal, ref) || 1;
  const anySelected = bars.some((b) => b.selected);
  const step = Math.max(1, Math.ceil(bars.length / maxLabels));
  const pct = (v: number) => `${Math.min(100, (v / top) * 100)}%`;
  // Few bars (periods) get a capped width and a wider gutter; many bars
  // (days) pack edge to edge.
  const few = bars.length <= 12;
  const cell = clsx('min-w-0 flex-1', few && 'max-w-[64px]');
  const gutter = few ? 'gap-2 sm:gap-3' : 'gap-px sm:gap-0.5';

  return (
    <figure className={className} aria-label={rest['aria-label']}>
      <div className="relative" style={{ height: height + 20 }}>
        {reference && ref > 0 && (
          <div
            aria-hidden
            className="absolute inset-x-0 z-[1] border-t border-dashed border-v2-fg-4/80"
            style={{ bottom: pct(ref) }}
          >
            <span className="v2-num absolute -top-[18px] right-0 rounded-v2-sm bg-v2-surface/90 px-1 text-v2-11 text-v2-fg-3">
              {reference.label}
            </span>
          </div>
        )}
        <ol
          className={clsx(
            'absolute inset-x-0 bottom-0 top-5 flex items-end',
            gutter,
          )}
        >
          {bars.map((b) => {
            const base = Math.min(b.value, ref > 0 ? ref : b.value);
            const over = ref > 0 ? Math.max(0, b.value - ref) : 0;
            const dim = anySelected && !b.selected;
            return (
              <li
                key={b.key}
                className={clsx('flex h-full flex-col justify-end', cell)}
                title={`${b.label}: ${format(b.value)}`}
                aria-label={`${b.label}: ${format(b.value)}`}
              >
                {over > 0 && (
                  <span
                    className={clsx(
                      'block w-full rounded-t-[2px]',
                      dim ? 'bg-v2-warning/20' : 'bg-v2-warning/40',
                    )}
                    style={{ height: pct(over) }}
                  />
                )}
                <span
                  className={clsx(
                    'block w-full',
                    over === 0 && 'rounded-t-[2px]',
                    dim ? 'bg-v2-accent/45' : 'bg-v2-accent',
                  )}
                  style={{ height: b.value > 0 ? pct(base) : 0 }}
                />
              </li>
            );
          })}
        </ol>
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 border-t border-v2-line"
        />
      </div>
      {/* Labels share the bars' flex grid; a shown label may overflow its
          narrow cell, so cells never clip and the last is right-anchored. */}
      <ol
        aria-hidden
        className={clsx(
          'mt-1.5 flex text-v2-11 leading-4 text-v2-fg-3',
          gutter,
        )}
      >
        {bars.map((b, i) => {
          const last = i === bars.length - 1 && bars.length > 1;
          const show =
            few || last || (i % step === 0 && bars.length - 1 - i >= step / 2);
          return (
            <li
              key={b.key}
              className={clsx(
                'v2-num relative h-4',
                cell,
                b.selected && 'font-medium text-v2-fg',
              )}
            >
              {show && (
                <span
                  className={clsx(
                    'absolute top-0 whitespace-nowrap',
                    few ? 'inset-x-0 text-center' : last ? 'right-0' : 'left-0',
                  )}
                >
                  {b.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </figure>
  );
};

export const BarsSkeleton: React.FC<{ bars?: number; height?: number }> = ({
  bars = 14,
  height = 160,
}) => (
  <div aria-busy>
    <div
      className="flex items-end gap-px border-b border-v2-line pt-5 sm:gap-0.5"
      style={{ height: height + 20 }}
    >
      {Array.from({ length: bars }).map((_, i) => (
        <span
          key={i}
          className="v2-skeleton block flex-1 rounded-t-[2px]"
          style={{ height: `${28 + ((i * 37) % 55)}%` }}
        />
      ))}
    </div>
    <div className="mt-1.5 flex gap-px sm:gap-0.5">
      {Array.from({ length: bars }).map((_, i) => (
        <span key={i} className="flex-1">
          {i % 3 === 0 && <span className="v2-skeleton block h-2.5 w-8" />}
        </span>
      ))}
    </div>
  </div>
);
